import { FC, useEffect, useMemo, useState } from "react";
import { Country, fetchEuCountries, fetchVatRate, VatRateResponse } from "../services/api";

type Props = {
  hsCode: string;
  isOpen: boolean;
};

export const VatRate: FC<Props> = ({ hsCode, isOpen }) => {
  const [euCountries, setEuCountries] = useState<Country[]>([]);
  const [euLoading, setEuLoading] = useState(false);
  const [euError, setEuError] = useState<string | null>(null);

  const [selectedCountryId, setSelectedCountryId] = useState<number | "">("");
  const [vatLoading, setVatLoading] = useState(false);
  const [vatError, setVatError] = useState<string | null>(null);
  const [vatRate, setVatRate] = useState<VatRateResponse | null>(null);

  const selectedCountryName = useMemo(() => {
    if (!selectedCountryId) return "";
    return euCountries.find((c) => c.id === Number(selectedCountryId))?.name ?? "";
  }, [selectedCountryId, euCountries]);

  // Load EU countries only when section opens
  useEffect(() => {
    if (!isOpen) return;
    if (euCountries.length > 0) return;

    setEuLoading(true);
    setEuError(null);

    fetchEuCountries()
      .then(setEuCountries)
      .catch((e) => {
        console.error(e);
        setEuError("Failed to load EU countries.");
      })
      .finally(() => setEuLoading(false));
  }, [isOpen, euCountries.length]);

  // Reset selection/result whenever section closes OR hsCode changes
  useEffect(() => {
    if (!isOpen) {
      setSelectedCountryId("");
      setVatRate(null);
      setVatError(null);
    }
  }, [isOpen]);

  useEffect(() => {
    setSelectedCountryId("");
    setVatRate(null);
    setVatError(null);
  }, [hsCode]);

  const onCountryChange = async (value: string) => {
    const id = value ? Number(value) : "";
    setSelectedCountryId(id);
    setVatRate(null);
    setVatError(null);

    if (!id) return;

    if (!hsCode?.trim()) {
      setVatError("HS code is missing.");
      return;
    }

    try {
      setVatLoading(true);
      const res = await fetchVatRate(hsCode, Number(id));
      setVatRate(res);
    } catch (e) {
      console.error(e);
      setVatError("Failed to fetch VAT rate.");
    } finally {
      setVatLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <section className="mt-6 bg-white rounded-2xl border border-neutral-200 shadow-xl p-6">
      <div className="max-w-[700px]">
        <div className="font-semibold mb-2">Destination country</div>

        {euLoading ? (
          <div className="text-neutral-500">Loading EU countries…</div>
        ) : euError ? (
          <div className="text-red-600">{euError}</div>
        ) : (
          <select
            className="w-full border border-neutral-300 rounded-lg px-3 py-2"
            value={selectedCountryId}
            onChange={(e) => onCountryChange(e.target.value)}
          >
            <option value="">Select a country…</option>
            {euCountries.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        )}

        <div className="mt-4 font-semibold">
          {vatLoading ? (
            <span className="text-neutral-500">Fetching VAT rate…</span>
          ) : vatError ? (
            <span className="text-red-600">{vatError}</span>
          ) : vatRate && selectedCountryId ? (
            <>
              Applicable VAT rate for the product in{" "}
              <span className="text-neutral-900">{selectedCountryName}</span> is :{" "}
              <span className="text-neutral-900">{vatRate.vatRate}%</span>
            </>
          ) : (
            <span className="text-neutral-500">Select a country to see the VAT rate.</span>
          )}
        </div>
      </div>
    </section>
  );
};
